import { useVModel } from "@vueuse/core";
import {
	computed,
	defineComponent,
	h,
	inject,
	nextTick,
	onMounted,
	onUnmounted,
	provide,
	ref,
	watch,
	type InjectionKey,
	type Ref,
} from "vue";
import { tw } from "../../lib/utils";

type StepperApi = {
	step: Ref<number | undefined>;

	/**
	 * Return the stepper index
	 */
	registerStep: (id: symbol) => number;
	unregisterStep: (id: symbol) => void;

	registerStepContent: (id: symbol) => number;
	unregisterStepContent: (id: symbol) => void;
};
const StepperSymbol: InjectionKey<StepperApi> = Symbol();

/**
 * @deprecated Use Stepper instead
 */
export const SoftStepper = defineComponent({
	name: "SoftStepper",
	props: { modelValue: Number },
	setup(props, { slots, emit }) {
		const valueModel = useVModel(props, "modelValue", emit, {
			passive: true,
			defaultValue: 0,
		});

		const steppers = new Set<symbol>();
		const steppersContent = new Set<symbol>();

		provide(StepperSymbol, {
			step: valueModel,
			registerStep(id) {
				steppers.add(id);
				return steppers.size - 1;
			},
			unregisterStep(id) {
				steppers.delete(id);
			},
			registerStepContent(id) {
				steppersContent.add(id);
				return steppersContent.size - 1;
			},
			unregisterStepContent(id) {
				steppersContent.delete(id);
			},
		});

		return () => h("div", { class: "w-full" }, slots.default?.());
	},
});

/**
 * @deprecated Use StepperList instead
 */
export const SoftStepperHeader = (_: any, { slots }) => {
	return h("div", { class: tw`steps w-full pt-6` }, slots.default?.());
};

/**
 * @deprecated Use StepperItem instead
 */
export const SoftStepperStep = defineComponent({
	name: "SoftStepperStep",
	props: { completed: Boolean, disabled: Boolean },
	setup(props, { slots, attrs }) {
		const id = Symbol();
		const api = inject(StepperSymbol);

		if (!api)
			throw new Error("[SoftStepperStep] Context not found. Please use inside a SoftStepper");

		const index = ref();
		onMounted(() => {
			index.value = api.registerStep(id);
		});
		onUnmounted(() => api.unregisterStep(id));

		const isCompleted = computed(() => {
			const currentStep = api.step.value;
			if (props.completed) return true;

			if (index.value != null && currentStep != null) {
				return index.value < currentStep;
			}

			return false;
		});

		const isClickable = computed(() => "onClick" in attrs);

		return () =>
			h(
				"div",
				{
					class: {
						"step focus-visible:outline-hidden": true,
						"step-primary": isCompleted.value,
						"cursor-pointer": isClickable.value,
					},
				},
				slots.default?.()
			);
	},
});

/**
 * @deprecated Use nothing instead
 */
export const SoftStepperItems = (_: any, { slots }) => {
	return h("div", { class: tw`relative overflow-hidden` }, slots.default?.());
};

/**
 * @deprecated Use StepperContent instead
 */
export const SoftStepperContent = defineComponent({
	setup(_, { slots }) {
		const api = inject(StepperSymbol);

		if (!api)
			throw new Error("[SoftStepperContent] Context not found. Please use inside a SoftStepper");

		const id = Symbol();
		const index = ref<number>();

		const animation = ref(false);

		onMounted(() => {
			index.value = api.registerStepContent(id);
			setTimeout(() => (animation.value = true));
		});
		onUnmounted(() => api.unregisterStep(id));

		const reverse = ref(false);

		const shouldRender = ref(false);

		watch(
			[api.step, index],
			([step], [previous]) => {
				if (index.value == null || previous == null || step == null) return;

				if (step < previous) {
					reverse.value = true;
				} else {
					reverse.value = false;
				}

				nextTick(() => (shouldRender.value = api.step.value === index.value));
			},
			{ immediate: true }
		);

		return () => {
			if (!shouldRender.value) {
				return null;
			}

			return h("div", null, slots.default?.());
		};
	},
});
